import React, { PureComponent } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import moment from "moment";

import Uri from "jsuri";

import {
  sortNumeric,
  selectFormat,
  TableContentCell,
  getMetricForColumn,
  Error,
} from "components/Utils/Utils.js";

import Heading from "components/Elements/Heading";

class YouTubeTopVideos extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderTable(data) {
    const { title, url } = this.props;

    const columns = [
      {
        dataField: "title",
        text: "Title",
        headerClasses: "sticky-column",
        classes: "sticky-column",
        style: { width: "320px", minWidth: "320px", textAlign: "left" },
        headerAlign: "left",
        formatter: TableContentCell,
        formatExtraData: {
          name: new Uri(url).hasQueryParam("p")
            ? new Uri(url).getQueryParamValue("p")
            : url.split("/")[2],
          showDate: false,
        },
        csvFormatter: (cell, row) => row && (row.title || row.tweet_text || ""),
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
      },
      {
        dataField: "network",
        text: "Network",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        formatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        headerAlign: "left",
        sort: true,
        csvExport: true,
      },
      {
        dataField: "published_date",
        text: "Post Date",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell, row) =>
          row && moment(row.published_date).format("YYYY-MM-DD"),
        headerAlign: "left",
        sort: true,
        csvExport: true,
        formatter: (cell, row) => {
          return moment(row.published_date).format("YYYY-MM-DD");
        },
        sortFunc: (a, b, order) => {
          if (order === "desc") {
            return new Date(b) - new Date(a);
          } else {
            return new Date(a) - new Date(b);
          }
        },
      },
      {
        dataField: "video_views",
        text: "Video Views",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "engagements",
        text: "Engagements",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "post_time",
        text: "Post Time",
        hidden: true,
      },
      {
        dataField: "url",
        text: "URL",
        hidden: true,
      },
      {
        dataField: "type",
        text: "Type",
        style: { width: "60px", minWidth: "60px", textTransform: "capitalize" },
        csvFormatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        formatter: (cell) => cell,
        sort: true,
      },
      {
        dataField: "username",
        text: "Handle",
        style: { width: "110px", minWidth: "110px" },
        csvFormatter: (cell) => cell,
        formatter: (cell) => cell,
        sort: true,
      },

      {
        dataField: "subscribers_gained",
        text: "Subs Gained",
        style: { width: "110px", minWidth: "110px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "subscribers_lost",
        text: "Subs Lost",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "dislikes",
        text: "Dislikes",
        style: { width: "100px", minWidth: "100px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "avg_completion",
        text: "Avg Completion %",
        style: { width: "130px", minWidth: "130px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },

      {
        dataField: "shares",
        text: "Shares",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "comments",
        text: "Comments",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "like_total",
        text: "Likes",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
    ];

    // add unique ids
    data.forEach((d, i) => (d.id = i));

    const defaultSorted = [
      {
        dataField: "published_date",
        order: "desc",
      },
    ];

    const paginate = paginationFactory({
      sizePerPage: 5,
      hideSizePerPage: true,
    });

    const filename = title.split(" ").join("_") || "spreadsheet";

    return (
      <ToolkitProvider
        keyField="id"
        data={data}
        columns={columns}
        striped={false}
        hover={false}
        bordered={false}
        exportCSV={{
          fileName: filename + ".csv",
          exportAll: false,
          onlyExportFiltered: true,
        }}
      >
        {(props) => (
          <BootstrapTable
            {...props.baseProps}
            {...props.csvProps}
            pagination={paginate}
            defaultSorted={defaultSorted}
            filter={filterFactory()}
            ref={(el) => (this.childRef = el)}
          />
        )}
      </ToolkitProvider>
    );
  }

  builtInExportCSV = () => this.childRef;

  render() {
    const { data } = this.props;

    if (data.hasData === false) {
      return <Error minHeight={560} />;
    }

    return (
      <>
        <Heading size={6} className="mb-3">
          Please note: Youtube provides data from 2 days ago.
        </Heading>

        {this.renderTable(data.data)}
      </>
    );
  }
}

export default YouTubeTopVideos;
